// ExplorationSpace.jsx - Version complète améliorée
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom'; 
import { 
    BookOpen, 
    Lightbulb, 
    Database, 
    CheckCircle, 
    ArrowRight, 
    Target,
    File,
    Upload,
    AlertTriangle,
    FileText,
    Sparkles,
    TrendingUp,
    Languages,
    Compass,
    GraduationCap
} from 'lucide-react';

import { doc, getDoc, collection, query, getDocs, addDoc, deleteDoc, orderBy, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { MathJaxContext } from 'better-react-mathjax';

import KnowledgeBase from '../CP-KnowledgeDB/knowledgeBase';
import ConceptBasedQuiz from '../CP-Quiz/ConceptBasedQuiz';
import TrainingWithGPT from './TrainingWithGPT';
import { NoteCard, NoteDetails, EmptyNotesList } from '../CP-HomePage/ExplorationNotes';
import { askGPT } from '../components/OpenAIService';
import SynthesisList from './SynthesisList';
import SynthesisViewer from './SynthesisViewer';
import SynthesisFirebaseService from './SynthesisFirebaseService';
import ExplorationNavigationCards from '../components/ExplorationNavigationCards';
import userPreferencesManager from '../components/UserPreferencesManager';

// Configuration MathJax
const mathJaxConfig = {
  loader: { load: ['[tex]/html'] },
  tex: {
    packages: { '[+]': ['html'] },
    inlineMath: [['$', '$']],
    displayMath: [['$$', '$$']]
  },
  startup: {
    typeset: false
  }
};

// ==================== COMPOSANTS UI ====================

// Composant Alert
const AlertMessage = ({ type, message, onClose }) => {
  const types = {
    error: {
      icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700'
    },
    success: {
      icon: <CheckCircle className="h-6 w-6 text-green-500" />,
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700'
    },
    info: {
      icon: <Lightbulb className="h-6 w-6 text-blue-500" />,
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700'
    }
  };
  
  const style = types[type] || types.info;
  
  return (
    <div className={`rounded-lg ${style.bg} ${style.border} border p-4 mb-6 relative`}>
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3 mt-0.5">
          {style.icon}
        </div>
        <div className={`${style.text} flex-grow`}>
          <p className="font-medium">{message}</p>
        </div>
        <button 
          onClick={onClose}
          className="ml-auto text-gray-400 hover:text-gray-500 transition-colors text-xl leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Composant carte de phase
const PhaseCard = ({ number, title, description, icon, isActive, onClick }) => {
  const icons = {
    database: <Database className="h-6 w-6 text-gray-600" />,
    bookopen: <BookOpen className="h-6 w-6 text-gray-600" />,
    graduation: <Sparkles className="h-6 w-6 text-gray-600" />
  };

  return (
    <div 
      className={`relative bg-white rounded-xl shadow-md overflow-hidden border ${
        isActive ? 'border-blue-300 ring-2 ring-blue-200' : 'border-gray-200'
      } hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer`}
      onClick={onClick}
    >
      <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center border border-blue-200">
        <span className="text-blue-600 font-semibold">{number}</span>
      </div>
      <div className="p-6 pt-14">
        <div className="mb-4 flex justify-center">
          <div className="p-3 bg-gray-100 rounded-full">
            {icons[icon]}
          </div>
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">{title}</h3>
        <p className="text-gray-600 text-center text-sm">{description}</p>
      </div>
      {isActive && (
        <div className="bg-blue-100 py-2 px-4 text-center">
          <span className="text-sm font-medium text-blue-700">Phase active</span>
        </div>
      )}
    </div>
  );
};

// Composant carte d'avantages
const AdvantageCard = ({ title, icon, gradient, color, bgColor, borderHover, features }) => {
  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 ${borderHover} hover:shadow-lg`}>
      <div className={`bg-gradient-to-r ${gradient} p-6 text-center`}>
        {icon}
        <h3 className="text-xl font-semibold text-gray-800 mt-3">
          {title}
        </h3>
      </div>
      <div className="p-6">
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <div className={`flex-shrink-0 h-5 w-5 rounded-full ${bgColor} flex items-center justify-center mt-0.5`}>
                <span className={`${color} text-xs`}>✓</span>
              </div>
              <span className="ml-3 text-gray-700 text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ==================== PAGE D'ACCUEIL ====================

const ExplorationLanding = ({ competence, phases, handlePhaseClick }) => {
  const avantages = [
    {
      title: "Base de connaissances personnalisée",
      icon: <Database className="h-12 w-12 text-blue-600 mx-auto" />,
      gradient: "from-blue-100 to-blue-50",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      borderHover: "hover:border-blue-300",
      features: [
        "Constitution progressive d'une base adaptée à vos objectifs",
        "Organisation automatique des connaissances par thèmes",
        "Accès permanent à vos ressources personnalisées"
      ]
    },
    {
      title: "Recherche sémantique avancée",
      icon: <Compass className="h-12 w-12 text-purple-600 mx-auto" />,
      gradient: "from-purple-100 to-purple-50",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      borderHover: "hover:border-purple-300",
      features: [
        "Moteur de recherche vectorielle pour des résultats pertinents",
        "Compréhension du contexte et de l'intention de vos requêtes",
        "Exploration par concepts et idées plutôt que par mots-clés"
      ]
    },
    {
      title: "Multilingue intégral",
      icon: <Languages className="h-12 w-12 text-green-600 mx-auto" />,
      gradient: "from-green-100 to-green-50",
      color: "text-green-600",
      bgColor: "bg-green-100",
      borderHover: "hover:border-green-300",
      features: [
        "Recherche et analyse dans plus de 20 langues",
        "Traduction instantanée des ressources pertinentes",
        "Découverte de sources internationales adaptées"
      ]
    },
    {
      title: "Apprentissage adaptatif",
      icon: <GraduationCap className="h-12 w-12 text-yellow-600 mx-auto" />,
      gradient: "from-yellow-100 to-yellow-50",
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
      borderHover: "hover:border-yellow-300",
      features: [
        "IA qui s'adapte à votre niveau et à vos objectifs",
        "Recommandations personnalisées de ressources",
        "Suivi de votre progression et ajustements intelligents"
      ]
    }
  ];
  
  return (
    <>
      {/* En-tête */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-6">
          <BookOpen className="h-8 w-8 text-blue-600" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
          Explorez, apprenez et maîtrisez de nouvelles compétences
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Créez votre propre base de connaissances et transformez-la en parcours d'apprentissage adaptatif avec notre IA
        </p>
      </div>
      
      {/* Parcours d'exploration */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Votre parcours d'exploration</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {phases.map((phase) => (
            <PhaseCard
              key={phase.number}
              number={phase.number}
              title={phase.title}
              description={phase.description}
              icon={phase.icon}
              isActive={phase.isActive}
              onClick={() => handlePhaseClick(phase.number)}
            />
          ))}
        </div>
      </div>
      
      {/* Avantages */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
          La puissance de l'exploration avec Aladinn
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {avantages.map((avantage, index) => (
            <AdvantageCard
              key={index}
              title={avantage.title}
              icon={avantage.icon}
              gradient={avantage.gradient}
              color={avantage.color}
              bgColor={avantage.bgColor}
              borderHover={avantage.borderHover}
              features={avantage.features}
            />
          ))}
        </div>
      </div>
    </>
  );
};

// ==================== PHASE 1: BASE DE CONNAISSANCES ====================

const KnowledgeBasePhase = ({ 
  competence, 
  userId, 
  knowledgeBasePresentation,
  loadingPresentation,
  explorationStats,
  onActivity 
}) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('upload');

  const renderMarkdown = (text) => {
    if (!text) return null;
    const blocks = text.split('\n\n');
    
    return blocks.map((block, index) => {
      let content = block;
      
      if (content.startsWith('###')) {
        content = content.replace(/^###\s*/, '');
        return <h3 key={index} className="text-lg font-bold text-gray-800 mb-3 mt-4">{content}</h3>;
      }
      
      if (content.startsWith('##')) {
        content = content.replace(/^##\s*/, '');
        return <h2 key={index} className="text-xl font-bold text-gray-800 mb-3 mt-4">{content}</h2>;
      }
      
      content = content.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      content = content.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      
      return (
        <p 
          key={index} 
          className="mb-4 text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    });
  };

  return (
    <div className="space-y-6">
      {/* Objectif */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Target className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Votre objectif</h3>
            <p className="text-gray-700 text-base leading-relaxed">{competence?.title}</p>
          </div>
        </div>
      </div>

      {/* Présentation de la base de connaissances */}
      {knowledgeBasePresentation && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center">
              <BookOpen className="h-6 w-6 mr-2 text-indigo-600" />
              Présentation de votre base de connaissances
            </h3>
            {loadingPresentation && (
              <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-indigo-400 border-t-indigo-200" />
            )}
          </div>
          <div className="prose prose-indigo max-w-none">
            {renderMarkdown(knowledgeBasePresentation)}
          </div>
        </div>
      )}

      {/* Cartes de navigation vers les fonctionnalités principales */}
      <ExplorationNavigationCards
        onNavigateToDocuments={() => setViewMode('documents')}
        onNavigateToSyntheses={() => setViewMode('syntheses')}
        onNavigateToNotes={() => setViewMode('notes')}
        onNavigateToQuiz={() => {
          // Navigation vers la phase d'apprentissage pour les quiz
          if (competence?.id) {
            navigate(`/exploration/${competence.id}/knowledgeTransform/${competence.id}`);
          }
        }}
        stats={{
          documentsCount: explorationStats.documentsCount,
          synthesesCount: 0, // À calculer si disponible
          notesCount: explorationStats.notesCount,
          quizCount: 0 // Les quiz n'ont pas de compteur spécifique
        }}
      />

      {/* Statistiques */}
      {explorationStats.documentsCount > 0 && (
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
          <div className="flex items-center mb-4">
            <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
            <h4 className="text-lg font-semibold text-gray-800">Votre progression</h4>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 text-center border border-blue-100">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {explorationStats.documentsCount}
              </div>
              <div className="text-sm text-gray-600 font-medium">Documents</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center border border-purple-100">
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {explorationStats.conceptsCount}
              </div>
              <div className="text-sm text-gray-600 font-medium">Concepts</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center border border-green-100">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {explorationStats.notesCount}
              </div>
              <div className="text-sm text-gray-600 font-medium">Notes</div>
            </div>
          </div>
        </div>
      )}

      {/* Recommandations pour bien démarrer */}
      {explorationStats.documentsCount === 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border-2 border-yellow-200">
          <h4 className="font-semibold text-yellow-800 mb-4 flex items-center text-lg">
            <Lightbulb className="h-5 w-5 mr-2" />
            Pour bien démarrer
          </h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Téléchargez quelques documents pertinents à votre objectif
              </p>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Explorez les concepts automatiquement identifiés
              </p>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Sauvegardez vos recherches importantes dans vos notes
              </p>
            </div>
            
            <div className="flex items-start gap-3 bg-white rounded-lg p-3 border border-yellow-100">
              <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-sm font-bold">4</span>
              </div>
              <p className="text-yellow-800 text-sm">
                Testez vos connaissances avec les quiz adaptatifs
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Interface de gestion des documents */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Database className="h-6 w-6 text-blue-600 mr-3" />
          <h3 className="text-xl font-semibold text-gray-800">Gérer vos documents</h3>
        </div>
        
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setViewMode('upload')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'upload' 
              ? 'bg-blue-100 text-blue-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Upload className="h-4 w-4" />
            Ajouter
          </button>
          <button
            onClick={() => setViewMode('documents')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'documents' 
              ? 'bg-blue-100 text-blue-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <File className="h-4 w-4" />
            Mes documents
          </button>
        </div>

        <KnowledgeBase 
          userId={userId} 
          competence={competence}
          initialViewMode={viewMode}
          onActivity={onActivity}
          showHeader={false}
          embedded={true}
        />
      </div>
    </div>
  );
};

// ==================== PHASE 2: EXPLORATION ====================

const ExplorationPhase = ({ 
  competence, 
  userId, 
  notes,
  loadingNotes,
  onNoteClick,
  onNoteDelete,
  onActivity 
}) => {
  const [viewMode, setViewMode] = useState('syntheses');
  const [syntheses, setSyntheses] = useState([]);
  const [loadingSyntheses, setLoadingSyntheses] = useState(false);
  const [selectedSynthesis, setSelectedSynthesis] = useState(null);

  // Charger les synthèses au montage
  useEffect(() => {
    const loadSyntheses = async () => {
      if (!userId || !competence?.id) return;
      
      setLoadingSyntheses(true);
      try {
        const result = await SynthesisFirebaseService.getUserSyntheses(userId, competence.id);
        if (result.success) {
          setSyntheses(result.syntheses || []);
        }
      } catch (error) {
        console.error('Erreur chargement synthèses:', error);
      } finally {
        setLoadingSyntheses(false);
      }
    };

    loadSyntheses();
  }, [userId, competence]);

  return (
    <div className="space-y-6">
      {/* Objectif */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Target className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Votre objectif</h3>
            <p className="text-gray-700 text-base leading-relaxed">{competence?.title}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <BookOpen className="h-6 w-6 text-blue-600 mr-3" />
          <h3 className="text-xl font-semibold text-gray-800">Explorer votre base de connaissances</h3>
        </div>
        
        <p className="text-gray-600 mb-6">
          Explorez votre base de connaissances avec notre moteur de recherche vectorielle. 
          Naviguez par concepts et sauvegardez vos découvertes importantes.
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setViewMode('syntheses')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'syntheses' 
              ? 'bg-indigo-100 text-indigo-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Sparkles className="h-4 w-4" />
            Synthèses ({syntheses.length})
          </button>
          <button
            onClick={() => setViewMode('documents')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'documents' 
              ? 'bg-blue-100 text-blue-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <File className="h-4 w-4" />
            Documents
          </button>
          <button
            onClick={() => setViewMode('concepts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'concepts' 
              ? 'bg-purple-100 text-purple-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Lightbulb className="h-4 w-4" />
            Concepts
          </button>
          <button
            onClick={() => setViewMode('notes')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'notes' 
              ? 'bg-yellow-100 text-yellow-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FileText className="h-4 w-4" />
            Mes notes ({notes.length})
          </button>
        </div>

        {viewMode === 'documents' ? (
          <KnowledgeBase 
            userId={userId} 
            competence={competence}
            initialViewMode="documents"
            onActivity={onActivity}
            showHeader={false}
            embedded={true}
          />
        ) : viewMode === 'concepts' ? (
          <>
            <KnowledgeBase 
              userId={userId} 
              competence={competence}
              initialViewMode="concepts"
              onActivity={onActivity}
              showHeader={false}
              embedded={true}
            />
            <div className="bg-blue-50 p-4 rounded-lg mt-6">
              <div className="flex items-center mb-2">
                <Lightbulb className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="font-medium text-blue-800">Conseil d'exploration</h4>
              </div>
              <p className="text-blue-700 text-sm">
                Utilisez le bouton "Sauvegarder cette recherche" pour conserver vos découvertes importantes dans vos notes.
              </p>
            </div>
          </>
        ) : viewMode === 'syntheses' ? (
          selectedSynthesis ? (
            <SynthesisViewer
              synthesis={selectedSynthesis}
              onBack={() => setSelectedSynthesis(null)}
              userId={userId}
              competenceId={competence?.id}
            />
          ) : (
            <SynthesisList
              userId={userId}
              competenceId={competence?.id}
              onSelectSynthesis={setSelectedSynthesis}
            />
          )
        ) : (
          <MathJaxContext config={mathJaxConfig}>
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FileText className="h-5 w-5 text-yellow-600 mr-2" />
                Vos recherches sauvegardées ({notes.length})
              </h4>
              
              {loadingNotes ? (
                <div className="flex justify-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-400 border-t-green-200" />
                </div>
              ) : notes.length > 0 ? (
                notes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    onClick={() => onNoteClick(note)}
                    onDelete={() => onNoteDelete(note.id)}
                  />
                ))
              ) : (
                <EmptyNotesList />
              )}
            </div>
          </MathJaxContext>
        )}
      </div>
    </div>
  );
};

// ==================== PHASE 3: APPRENTISSAGE ====================

const LearningPhase = ({ competence, userId, onProgressUpdate }) => {
  const [activeTab, setActiveTab] = useState('quiz');

  return (
    <div className="space-y-6">
      {/* Objectif */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Target className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Votre objectif</h3>
            <p className="text-gray-700 text-base leading-relaxed">{competence?.title}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Sparkles className="h-6 w-6 text-blue-600 mr-3" />
          <h3 className="text-xl font-semibold text-gray-800">Valider vos connaissances</h3>
        </div>
        
        <p className="text-gray-600 mb-6">
          Transformez vos connaissances en compétences avec nos quiz adaptatifs et exercices pratiques.
        </p>
        
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('quiz')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'quiz' 
              ? 'bg-orange-100 text-orange-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Target className="h-4 w-4" />
            Quiz
          </button>
          <button
            onClick={() => setActiveTab('exercices')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'exercices' 
              ? 'bg-orange-100 text-orange-700 font-medium' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Sparkles className="h-4 w-4" />
            Exercices pratiques
          </button>
        </div>

        {activeTab === 'quiz' ? (
          <ConceptBasedQuiz 
            competence={competence} 
            userId={userId} 
            onQuizComplete={onProgressUpdate} 
          />
        ) : (
          <TrainingWithGPT 
            competence={competence} 
            userId={userId} 
            onExerciseComplete={onProgressUpdate} 
          />
        )}
      </div>
    </div>
  );
};

// ==================== COMPOSANT PRINCIPAL ====================

const ExplorationSpace = () => {
  const { competenceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  // États principaux
  const [currentCompetence, setCurrentCompetence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState(null);
  const [activePhase, setActivePhase] = useState(0);
  
  // États pour les notes
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  
  // États pour les statistiques
  const [explorationStats, setExplorationStats] = useState({
    documentsCount: 0,
    conceptsCount: 0,
    notesCount: 0
  });
  
  // État pour la présentation
  const [knowledgeBasePresentation, setKnowledgeBasePresentation] = useState(null);
  const [loadingPresentation, setLoadingPresentation] = useState(false);

  // ==================== CHARGEMENT DE LA COMPÉTENCE ====================

  useEffect(() => {
    const fetchCompetence = async () => {
      if (!competenceId || !user) {
        setLoading(false);
        if (!user) {
          setAlertMessage({ type: 'error', message: 'Vous devez être connecté pour accéder à cette page' });
        }
        return;
      }
      
      try {
        setLoading(true);
        const docSnap = await getDoc(doc(db, 'users', user.uid, 'competences', competenceId));
        
        if (docSnap.exists()) {
          const competenceData = { ...docSnap.data(), id: competenceId };
          setCurrentCompetence(competenceData);

          // Persister comme objectif courant + marquer dernier accès
          try {
            await userPreferencesManager.setCurrentObjective(user.uid, {
              id: competenceData.id,
              title: competenceData.title || competenceData.objectiveTitle || '',
              theme: competenceData.theme || competenceData.domain || competenceData.chapitre || null,
              chapitre: competenceData.chapitre || null
            });
            await userPreferencesManager.updateLastAccessed(user.uid);
          } catch (e) {
            console.error('ExplorationSpace: erreur persistance objectif courant:', e);
          }
          
          // Charger les statistiques
          setExplorationStats({
            documentsCount: competenceData.documentsCount || 0,
            conceptsCount: competenceData.conceptsCount || 0,
            notesCount: competenceData.notesCount || 0
          });
        } else {
          setAlertMessage({ type: 'error', message: 'Compétence non trouvée' });
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        setAlertMessage({ type: 'error', message: 'Erreur lors du chargement de la compétence' });
      } finally {
        setLoading(false);
      }
    };
    
    if (!user) {
      navigate('/');
    } else {
      fetchCompetence();
    }
  }, [competenceId, user, navigate]);

  // ==================== CHARGEMENT DES NOTES ====================

  const loadNotes = useCallback(async () => {
    if (!user || !competenceId) return;
    
    setLoadingNotes(true);
    try {
      const notesRef = collection(db, 'users', user.uid, 'competences', competenceId, 'notes');
      const q = query(notesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const loadedNotes = [];
      querySnapshot.forEach((doc) => {
        loadedNotes.push({ id: doc.id, ...doc.data() });
      });
      
      setNotes(loadedNotes);
      setExplorationStats(prev => ({ ...prev, notesCount: loadedNotes.length }));
    } catch (error) {
      console.error('Erreur chargement notes:', error);
      if (error.code === 'permission-denied') {
        console.warn('Permissions insuffisantes pour les notes');
        setNotes([]);
      }
    } finally {
      setLoadingNotes(false);
    }
  }, [user, competenceId]);

  useEffect(() => {
    if (user && competenceId) {
      loadNotes();
    }
  }, [loadNotes, user, competenceId]);

  // ==================== GÉNÉRATION DE LA PRÉSENTATION ====================

  const generateKnowledgeBasePresentation = useCallback(async () => {
    if (!currentCompetence?.title || !user) return;
    
    setLoadingPresentation(true);
    try {
      const docRef = doc(db, 'users', user.uid, 'competences', currentCompetence.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists() && docSnap.data().knowledgeBasePresentation) {
        setKnowledgeBasePresentation(docSnap.data().knowledgeBasePresentation);
      } else {
        const prompt = `Créer une présentation structurée de la base de connaissances pour l'objectif suivant : "${currentCompetence.title}".
        
        La présentation doit inclure :
        1. Une introduction expliquant l'importance de cette base de connaissances
        2. Les domaines clés à couvrir (3-5 domaines)
        3. Les bénéfices attendus de cette exploration
        
        Retourne le texte en 250 mots maximum, organisé en paragraphes clairs séparés par deux sauts de ligne.
        Utilise un ton professionnel mais accessible.`;

        const response = await askGPT(prompt);
        if (response) {
          setKnowledgeBasePresentation(response);
          await updateDoc(docRef, {
            knowledgeBasePresentation: response,
            knowledgeBasePresentationDate: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error('Erreur génération présentation:', error);
    } finally {
      setLoadingPresentation(false);
    }
  }, [currentCompetence, user]);

  useEffect(() => {
    if (user && currentCompetence?.id && activePhase === 1) {
      generateKnowledgeBasePresentation();
    }
  }, [user, currentCompetence, activePhase, generateKnowledgeBasePresentation]);

  // ==================== DÉTECTION DE LA PHASE ACTIVE ====================

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/dataCollection/')) {
      setActivePhase(1);
    } else if (path.includes('/semanticSearch/')) {
      setActivePhase(2);
    } else if (path.includes('/knowledgeTransform/')) {
      setActivePhase(3);
    } else if (path.includes('/exploration/')) {
      setActivePhase(0);
    }
  }, [location.pathname]);

  // ==================== GESTION DES ACTIVITÉS ====================

  const handleKnowledgeBaseActivity = useCallback((activity) => {
    switch (activity.type) {
      case 'document_uploaded':
        setExplorationStats(prev => ({ 
          ...prev, 
          documentsCount: prev.documentsCount + 1 
        }));
        break;
      case 'concept_explored':
        // Mise à jour si nécessaire
        break;
      case 'save_search_as_note':
        if (activity.query) {
          saveSearchAsNote(
            activity.query, 
            activity.resultsCount, 
            activity.synthesis,
            activity.documents || []
          );
        }
        break;
      default:
        break;
    }
  }, []);

  // ==================== SAUVEGARDE D'UNE RECHERCHE ====================

  const saveSearchAsNote = useCallback(async (searchQuery, searchResults, synthesis, documents) => {
    if (!user || !competenceId || !searchQuery) return;
    
    try {
      const notesRef = collection(db, 'users', user.uid, 'competences', competenceId, 'notes');
      
      console.log('📝 Documents reçus pour sauvegarde:', documents);
      
      const linkedDocuments = documents.map(doc => {
        const docData = {
          id: doc.id || doc.documentId || doc._id,
          title: doc.title || doc.fileName || doc.name || 'Document sans titre',
          fileName: doc.fileName || doc.title || doc.name,
          fileUrl: doc.fileUrl || doc.url || doc.downloadURL || doc.path || null,
          score: doc.score || doc.similarity || doc.relevance || 0,
          excerpt: doc.excerpt || doc.content?.substring(0, 300) || doc.text?.substring(0, 300) || null,
          pageNumber: doc.pageNumber || doc.page || null
        };
        
        console.log('📄 Document traité:', docData);
        return docData;
      });
      
      console.log('✅ Documents liés finaux:', linkedDocuments);
      
      const noteData = {
        title: searchQuery,
        searchQuery: searchQuery,
        content: searchQuery,
        type: 'search',
        resultsCount: searchResults || 0,
        synthesis: synthesis ? {
          summary: typeof synthesis === 'string' ? synthesis : synthesis.summary || ''
        } : null,
        linkedDocuments: linkedDocuments,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('💾 Note à sauvegarder:', noteData);
      
      await addDoc(notesRef, noteData);
      await loadNotes();
      
      setAlertMessage({ 
        type: 'success', 
        message: `✅ Recherche sauvegardée avec ${linkedDocuments.length} document(s) !` 
      });
      setTimeout(() => setAlertMessage(null), 3000);
      
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la note:', error);
      
      if (error.code === 'permission-denied') {
        setAlertMessage({ type: 'error', message: '❌ Permissions insuffisantes pour sauvegarder les notes.' });
      } else {
        setAlertMessage({ type: 'error', message: '❌ Erreur lors de la sauvegarde de la note' });
      }
      setTimeout(() => setAlertMessage(null), 5000);
    }
  }, [user, competenceId, loadNotes]);

  // ==================== SUPPRESSION D'UNE NOTE ====================

  const deleteNote = useCallback(async (noteId) => {
    if (!user || !competenceId || !noteId) return;
    
    try {
      const noteRef = doc(db, 'users', user.uid, 'competences', competenceId, 'notes', noteId);
      await deleteDoc(noteRef);
      await loadNotes();
      
      setAlertMessage({ type: 'success', message: '✅ Note supprimée' });
      setTimeout(() => setAlertMessage(null), 2000);
    } catch (error) {
      console.error('Erreur lors de la suppression de la note:', error);
      setAlertMessage({ type: 'error', message: '❌ Erreur lors de la suppression' });
      setTimeout(() => setAlertMessage(null), 3000);
    }
  }, [user, competenceId, loadNotes]);

  // ==================== NAVIGATION ENTRE PHASES ====================

  const handlePhaseClick = (phaseNumber) => {
    if (!competenceId) {
      setAlertMessage({ type: 'error', message: 'Impossible de naviguer : ID de compétence manquant' });
      return;
    }
    
    const routes = {
      1: `/exploration/${competenceId}/dataCollection/${competenceId}`,
      2: `/exploration/${competenceId}/semanticSearch/${competenceId}`,
      3: `/exploration/${competenceId}/knowledgeTransform/${competenceId}`
    };
    
    if (routes[phaseNumber]) {
      navigate(routes[phaseNumber]);
    }
  };

  // ==================== DÉFINITION DES PHASES ====================

  const phases = [
    {
      number: 1,
      title: "Votre base de connaissance",
      description: "Constituez votre base de connaissances personnalisée à partir de sources diversifiées",
      icon: "database",
      isActive: activePhase === 1
    },
    {
      number: 2,
      title: "Exploration",
      description: "Explorez votre base avec notre moteur de recherche vectorielle multilingue",
      icon: "bookopen",
      isActive: activePhase === 2
    },
    {
      number: 3,
      title: "Apprentissage adaptatif",
      description: "Transformez vos connaissances en compétences avec quiz et exercices",
      icon: "graduation",
      isActive: activePhase === 3
    }
  ];

  // ==================== RENDU CONDITIONNEL ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-400 border-t-blue-200 mb-4"></div>
          <p className="text-gray-600 font-medium">Chargement de votre espace d'exploration...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-md max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Authentification requise</h2>
          <p className="text-gray-600 mb-6">Vous devez être connecté pour accéder à cette page.</p>
          <button 
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  if (!currentCompetence && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-md max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Objectif non trouvé</h2>
          <p className="text-gray-600 mb-6">La compétence que vous recherchez n'existe pas ou n'est pas accessible.</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // ==================== RENDU PRINCIPAL ====================

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Messages d'alerte */}
          {alertMessage && (
            <AlertMessage 
              type={alertMessage.type} 
              message={alertMessage.message}
              onClose={() => setAlertMessage(null)}
            />
          )}

          {/* Fil d'Ariane pour la navigation */}
          {activePhase > 0 && (
            <div className="mb-6">
              <nav className="flex items-center space-x-2 text-sm">
                <button
                  onClick={() => navigate(`/exploration/${competenceId}`)}
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  Parcours d'exploration
                </button>
                <span className="text-gray-400">/</span>
                <span className="text-gray-700 font-medium">
                  {activePhase === 1 && 'Base de connaissances'}
                  {activePhase === 2 && 'Exploration'}
                  {activePhase === 3 && 'Apprentissage'}
                </span>
              </nav>
            </div>
          )}

          {/* Contenu selon la phase active */}
          {activePhase === 0 && (
            <ExplorationLanding 
              competence={currentCompetence}
              phases={phases}
              handlePhaseClick={handlePhaseClick}
            />
          )}

          {activePhase === 1 && (
            <KnowledgeBasePhase
              competence={currentCompetence}
              userId={user.uid}
              knowledgeBasePresentation={knowledgeBasePresentation}
              loadingPresentation={loadingPresentation}
              explorationStats={explorationStats}
              onActivity={handleKnowledgeBaseActivity}
            />
          )}

          {activePhase === 2 && (
            <ExplorationPhase
              competence={currentCompetence}
              userId={user.uid}
              notes={notes}
              loadingNotes={loadingNotes}
              onNoteClick={(note) => {
                setSelectedNote(note);
                setShowNoteDialog(true);
              }}
              onNoteDelete={deleteNote}
              onActivity={handleKnowledgeBaseActivity}
            />
          )}

          {activePhase === 3 && (
            <LearningPhase
              competence={currentCompetence}
              userId={user.uid}
              onProgressUpdate={() => {
                console.log('Progression mise à jour');
              }}
            />
          )}

          {/* Dialog pour afficher le détail d'une note */}
          {showNoteDialog && selectedNote && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => {
                setShowNoteDialog(false);
                setSelectedNote(null);
              }}
            >
              <div 
                className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* En-tête du dialog */}
                <div className="flex items-center justify-between border-b border-gray-200 p-6">
                  <div className="flex items-center text-lg font-semibold">
                    <FileText className="h-5 w-5 text-green-600 mr-2" />
                    <span className="truncate">{selectedNote.title}</span>
                  </div>
                  <button
                    onClick={() => {
                      setShowNoteDialog(false);
                      setSelectedNote(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-3xl leading-none transition-colors ml-4"
                  >
                    ×
                  </button>
                </div>
                
                {/* Contenu du dialog */}
                <div className="p-6 overflow-y-auto flex-1">
                  <NoteDetails note={selectedNote} />
                </div>
                
                {/* Pied du dialog */}
                <div className="border-t border-gray-200 p-4 flex justify-end">
                  <button
                    onClick={() => {
                      setShowNoteDialog(false);
                      setSelectedNote(null);
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </MathJaxContext>
  );
};

export default ExplorationSpace;
